export const PLAYER_STATES = {
  BUFFERING: 3,
  CUED: 5,
  ENDED: 0,
  PAUSED: 2,
  PLAYING: 1,
  UNSTARTED: -1,
} as const;

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export type PlaybackRate = (typeof PLAYBACK_RATES)[number];
export type YouTubeApiStatus = 'error' | 'idle' | 'loading' | 'ready';

export interface VideoPlayerHandle {
  getCurrentTime(): number;
  getDuration(): number;
  getPlaybackRate(): number;
  getPlayerState(): number;
  getVolume(): number;
  isMuted(): boolean;
  loadVideoById(videoId: string): void;
  mute(): void;
  pause(): void;
  play(): void;
  seek(seconds: number): void;
  setPlaybackRate(rate: PlaybackRate): void;
  setVolume(volume: number): void;
  unMute(): void;
}

export interface VideoPlayerSnapshot {
  currentTime: number;
  duration: number;
  isMuted: boolean;
  playbackRate: PlaybackRate;
  playerState: number;
  volume: number;
}

export interface YouTubePlayerInstance {
  destroy(): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlaybackRate(): number;
  getPlayerState(): number;
  getVolume(): number;
  isMuted(): boolean;
  loadVideoById(videoId: string): void;
  mute(): void;
  pauseVideo(): void;
  playVideo(): void;
  seekTo(seconds: number, allowSeekAhead?: boolean): void;
  setPlaybackRate(rate: number): void;
  setVolume(volume: number): void;
  stopVideo?(): void;
  unMute(): void;
}

export interface YouTubePlayerEvent {
  target: YouTubePlayerInstance;
}

export interface YouTubePlayerStateChangeEvent extends YouTubePlayerEvent {
  data: number;
}

export interface YouTubePlayerOptions {
  events?: {
    onError?: (event: unknown) => void;
    onReady?: (event: YouTubePlayerEvent) => void;
    onStateChange?: (event: YouTubePlayerStateChangeEvent) => void;
  };
  playerVars?: Record<string, number | string>;
  videoId: string;
}

export interface YouTubePlayerConstructor {
  new (
    element: HTMLElement | string,
    options: YouTubePlayerOptions,
  ): YouTubePlayerInstance;
}

export interface YouTubeGlobal {
  Player: YouTubePlayerConstructor;
  PlayerState: typeof PLAYER_STATES;
}

declare global {
  interface Window {
    YT?: YouTubeGlobal;
    onYouTubeIframeAPIReady?: (() => void) | undefined;
  }
}
